import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  CircularProgress,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Chip,
  Divider,
  FormGroup,
  FormControlLabel,
  Switch
} from '@mui/material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext.js';
import { useFeatureFlags } from '../../context/FeatureFlagContext';
import installBaseService from '../../services/installBase.service';
import CustomerFilter from './filters/CustomerFilter';
import EquipmentFilter from './filters/EquipmentFilter';
import LocationFilter from './filters/LocationFilter';
import StatusFilter from './filters/StatusFilter';
import ServiceFilter from './filters/ServiceFilter';
import LocationMarker from './markers/LocationMarker';
import { apiService } from '../../services/api.service';
import LocationDetailsDialog from './dialogs/LocationDetailsDialog';
import createBladeIcon from './markers/BladeIcon';
import './InstallBaseMap.css';

const InstallBaseMap = () => {
  const { customerId } = useParams();
  // Map refs
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersLayerRef = useRef(null);
  const linesLayerRef = useRef(null);
  const legendRef = useRef(null);
  
  // Context hooks
  const { isAuthenticated } = useAuth();
  const { tenant, isLoading: isTenantLoading, error: tenantError } = useTenant();
  const { isFeatureEnabled: isInstallBaseEnabled } = useFeatureFlags();
  
  // State declarations
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [equipmentModels, setEquipmentModels] = useState([]);
  const [installations, setInstallations] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(customerId || '');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedLocationData, setSelectedLocationData] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [serviceDays, setServiceDays] = useState(30);
  const [showNeedingService, setShowNeedingService] = useState(false);
  
  // Fetch data from backend
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch customers and location details in parallel
      const [customersResponse, locationsResponse] = await Promise.all([
        installBaseService.getCustomers(),
        installBaseService.getAllLocationDetails()
      ]);
      
      console.log('Raw API Response - Locations:', locationsResponse);
      
      // Check if we have installations data
      const hasInstallations = locationsResponse.some(loc => 
        loc.installations_json && loc.installations_json.length > 0
      );
      console.log('Locations have installations data:', hasInstallations);
      
      if (locationsResponse && locationsResponse.length > 0) {
        console.log('Sample location data:', {
          name: locationsResponse[0].name,
          installations: locationsResponse[0].installations_json,
          hasInstallationsField: 'installations_json' in locationsResponse[0]
        });
      }
      
      setCustomers(customersResponse);
      setLocations(locationsResponse || []);
      
      // If customerId is provided, set it as the selected customer
      if (customerId) {
        setSelectedCustomer(customerId);
      }
      
      // Update the map with all locations, filtering will be handled by applyFilters
      if (mapInstanceRef.current && locationsResponse && locationsResponse.length > 0) {
        updateMap(locationsResponse, []);
      }
      
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };
  
  // Update map with locations and installations
  const updateMap = (locationsData, installationsData) => {
    // Clear existing markers
    markersLayerRef.current.clearLayers();
    
    const locationsWithoutCoordinates = [];
    
    // Create custom data center icon
    const createDataCenterIcon = (isActive, alertCount = 0, hasHighAlert = false) => {
      const iconSize = 24;
      const backgroundColor = hasHighAlert ? '#ff4444' : (isActive ? '#4CAF50' : '#9e9e9e');
      const iconHtml = `
        <div style="
          width: ${iconSize}px;
          height: ${iconSize}px;
          background-color: ${backgroundColor};
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 12px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          border: 2px solid white;
          position: relative;
        ">
          ${alertCount > 0 ? alertCount : ''}
          ${hasHighAlert ? `
            <div style="
              position: absolute;
              top: -2px;
              right: -2px;
              width: 8px;
              height: 8px;
              background-color: #ff0000;
              border-radius: 50%;
              border: 1px solid white;
            "></div>
          ` : ''}
        </div>
      `;
      
      return L.divIcon({
        html: iconHtml,
        className: 'custom-data-center-icon',
        iconSize: [iconSize, iconSize],
        iconAnchor: [iconSize/2, iconSize/2],
        popupAnchor: [0, -iconSize/2]
      });
    };
    
    // Add location markers
    locationsData.forEach(location => {
      if (location.latitude && location.longitude) {
        // Get alert information
        const alertCount = location.total_active_alerts || 0;
        const hasHighAlert = location.high_severity_count > 0;
        const hasActiveInstallations = location.active_installation_count > 0;
        
        const marker = L.marker([location.latitude, location.longitude], {
          icon: createDataCenterIcon(hasActiveInstallations, alertCount, hasHighAlert)
        });
        
        marker.on('click', () => {
          setSelectedLocationData(location);
          setIsDialogOpen(true);
        });
        
        // Add popup with installation links
        const popupContent = `
          <div style="min-width: 200px;">
            <strong>${location.location_name}</strong><br>
            <strong>Location ID:</strong> ${location.location_id}<br>
            <strong>Address:</strong> ${location.address_line1 || ''}${location.address_line2 ? `, ${location.address_line2}` : ''}<br>
            <strong>City:</strong> ${location.city}, ${location.state}<br>
            <div style="margin: 8px 0; padding: 8px; background-color: #f5f5f5; border-radius: 4px;">
              <strong>Alert Summary:</strong><br>
              <span style="color: #ff4444;">High: ${location.high_severity_count || 0}</span><br>
              <span style="color: #ffa726;">Medium: ${location.medium_severity_count || 0}</span><br>
              <span style="color: #66bb6a;">Low: ${location.low_severity_count || 0}</span><br>
              <strong>Total Active Alerts:</strong> ${location.total_active_alerts || 0}
            </div>
            <strong>Installations:</strong><br>
            ${location.installations_json ? location.installations_json.map(inst => 
              `<a href="/installations/${inst.installation_id}" style="color: #1976d2; text-decoration: none; display: block; margin: 4px 0;">
                • ${inst.serial_number} (${inst.status})
              </a>`
            ).join('') : 'No installations'}
          </div>
        `;
        
        marker.bindPopup(popupContent);
        markersLayerRef.current.addLayer(marker);
      } else {
        locationsWithoutCoordinates.push(location.name);
      }
    });
    
    // Show warning for locations without coordinates
    if (locationsWithoutCoordinates.length > 0) {
      console.warn(`${locationsWithoutCoordinates.length} locations are missing coordinates and cannot be displayed on the map.`);
      setError(`Some locations (${locationsWithoutCoordinates.length}) cannot be displayed on the map because they are missing coordinates.`);
    }
    
    // Fit map bounds to all markers
    if (markersLayerRef.current.getLayers().length > 0) {
      const group = L.featureGroup(markersLayerRef.current.getLayers());
      mapInstanceRef.current.fitBounds(group.getBounds());
    } else {
      console.warn('No markers were added to the map.');
    }
    
    // Update the legend
    updateLegend();
  };
  
  // Update legend based on current filters
  const updateLegend = () => {
    // Remove existing legend if it exists
    if (legendRef.current) {
      legendRef.current.remove();
    }

    const legend = L.control({ position: 'bottomright' });

    legend.onAdd = () => {
      const div = L.DomUtil.create('div', 'info legend');
      div.style.backgroundColor = 'white';
      div.style.padding = '10px';
      div.style.border = '1px solid #ccc';
      div.style.borderRadius = '4px';
      div.style.marginBottom = '10px';

      console.log('Calculating active locations...');
      const activeLocations = locations.filter(loc => {
        const installations = loc.installations_json || [];
        console.log(`Location ${loc.name} installations:`, installations);
        const hasActive = installations.some(install => install.status?.toLowerCase() === 'active');
        console.log(`Location ${loc.name} has active:`, hasActive);
        return hasActive;
      }).length;
      
      console.log('Total active locations:', activeLocations);
      console.log('Total locations:', locations.length);
      const inactiveLocations = locations.length - activeLocations;

      div.innerHTML = `
        <div style="margin-bottom: 5px;"><strong>Location Status</strong></div>
        <div style="display: flex; align-items: center; margin-bottom: 3px;">
          <div style="width: 20px; height: 20px; margin-right: 5px;">
            ${createBladeIcon(true)}
          </div>
          Active (${activeLocations})
        </div>
        <div style="display: flex; align-items: center;">
          <div style="width: 20px; height: 20px; margin-right: 5px;">
            ${createBladeIcon(false)}
          </div>
          Inactive (${inactiveLocations})
        </div>
      `;

      return div;
    };

    legendRef.current = legend;
    legend.addTo(mapInstanceRef.current);
  };
  
  // Apply filters to the map
  const applyFilters = () => {
    // Start with all locations
    let filteredLocations = [...locations];
    
    // Filter locations based on selected customer
    if (selectedCustomer) {
      filteredLocations = filteredLocations.filter(
        loc => loc.customer_id === selectedCustomer
      );
    }
    
    // Filter installations based on selected location and other criteria
    let filteredInstallations = [...installations];
    
    if (selectedStatus) {
      filteredInstallations = filteredInstallations.filter(
        inst => inst.status === selectedStatus
      );
    }
    
    if (showNeedingService) {
      filteredInstallations = filteredInstallations.filter(
        inst => inst.days_since_service > serviceDays
      );
    }
    
    console.log('Applying filters:', {
      totalLocations: locations.length,
      filteredLocations: filteredLocations.length,
      totalInstallations: installations.length,
      filteredInstallations: filteredInstallations.length,
      selectedCustomer,
      selectedStatus
    });
    
    // Update map with filtered data
    updateMap(filteredLocations, filteredInstallations);
  };
  
  // Reset all filters
  const resetFilters = () => {
    setSelectedCustomer('');
    setSelectedStatus('');
    setServiceDays(30);
    setShowNeedingService(false);
    
    // Update map with all data
    console.log('Resetting filters, showing all installations:', installations.length);
    updateMap(locations, installations);
  };
  
  // Update map when data or filters change
  useEffect(() => {
    if (mapInstanceRef.current && locations.length > 0) {
      // Apply customer filter if one is selected
      const filteredLocations = selectedCustomer
        ? locations.filter(loc => loc.customer_id === selectedCustomer)
        : locations;
      updateMap(filteredLocations, installations);
    }
  }, [locations, installations, showNeedingService, selectedCustomer]);

  // Fetch data when showNeedingService changes
  useEffect(() => {
    fetchData();
  }, [showNeedingService]);

  // Update useEffect to re-fetch when customerId changes
  useEffect(() => {
    if (customerId) {
      setSelectedCustomer(customerId);
    }
    fetchData();
  }, [customerId]);

  // Initial data fetch
  useEffect(() => {
    fetchData();
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Initialize map
  useEffect(() => {
    if (mapRef.current && !mapInstanceRef.current) {
      // Initialize the map
      mapInstanceRef.current = L.map(mapRef.current).setView([39.5, -98.35], 4);
      
      // Add tile layer
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors'
      }).addTo(mapInstanceRef.current);
      
      // Initialize markers layer
      markersLayerRef.current = L.layerGroup().addTo(mapInstanceRef.current);
      
      // Initialize lines layer
      linesLayerRef.current = L.layerGroup().addTo(mapInstanceRef.current);
      
      // Update legend
      updateLegend();
    }
    
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);
  
  // Update legend when filters change
  useEffect(() => {
    if (mapInstanceRef.current) {
      updateLegend();
    }
  }, [showNeedingService, mapInstanceRef.current]);
  
  // Check feature flag first
  if (!isInstallBaseEnabled) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        bgcolor: 'background.default'
      }}>
        <Typography variant="h6" color="error">
          Install Base feature is not enabled for your account.
        </Typography>
      </Box>
    );
  }

  // Handle tenant loading state
  if (isTenantLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        bgcolor: 'background.default'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  // Handle tenant error state
  if (tenantError) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        bgcolor: 'background.default',
        gap: 2
      }}>
        <Typography variant="h6" color="error">
          {tenantError}
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => window.location.reload()}
        >
          Retry
        </Button>
      </Box>
    );
  }
  
  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case 'customer':
        setSelectedCustomer(value || '');  // Ensure empty string is set when no customer is selected
        // Automatically apply filters when customer changes
        const filteredLocations = value 
          ? locations.filter(loc => loc.customer_id === value)
          : locations;
        updateMap(filteredLocations, installations);
        break;
      case 'status':
        setSelectedStatus(value);
        break;
      default:
        break;
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedLocationData(null);
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5">
            Install Base Map
          </Typography>
        </Box>
        
        {!isInstallBaseEnabled ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="error">
              Install Base feature is not enabled for this tenant
            </Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              Please contact your administrator to enable the Install Base feature.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Filters Section */}
            <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <CustomerFilter 
                    customers={customers}
                    selectedCustomer={selectedCustomer}
                    onChange={setSelectedCustomer}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <StatusFilter
                    selectedStatus={selectedStatus}
                    onChange={setSelectedStatus}
                    installations={installations}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ServiceFilter 
                    serviceDays={serviceDays}
                    showNeedingService={showNeedingService}
                    onDaysChange={setServiceDays}
                    onToggleChange={setShowNeedingService}
                  />
                </Grid>
                <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                  <Button 
                    variant="outlined" 
                    color="secondary" 
                    onClick={resetFilters}
                    sx={{ mr: 1 }}
                  >
                    Reset Filters
                  </Button>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={applyFilters}
                  >
                    Apply Filters
                  </Button>
                </Grid>
              </Grid>
            </Box>
            
            {/* Map Section */}
            <Box
              ref={mapRef}
              sx={{
                height: '600px',
                width: '100%',
                position: 'relative',
                '& .leaflet-container': {
                  height: '100%',
                  width: '100%'
                }
              }}
            >
              {loading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    zIndex: 1000
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
              
              {error && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    zIndex: 1000,
                    gap: 2
                  }}
                >
                  <Typography color="error">{error}</Typography>
                  <Button variant="contained" onClick={fetchData}>
                    Retry
                  </Button>
                </Box>
              )}
            </Box>
          </>
        )}
      </Paper>
      
      <LocationDetailsDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        locationData={selectedLocationData}
      />
    </Box>
  );
};

export default InstallBaseMap;
